import { Show } from "@refinedev/antd";
import { Authenticated, useOne } from "@refinedev/core";
import { Flex } from "antd";
import HtmlPageTitle from "components/HtmlPageTitle/";
import { useAntTheme } from "hooks/useAntTheme/";
import { useSession } from "hooks/useSession/";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useBoundStore } from "@/store";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { User } from "types/";
import { InstanceSwitch } from "./components/InstanceSwitch";
import { ProjectError } from "./components/ProjectError";
import { ProjectHeaderNew } from "./components/ProjectHeaderNew";
import { ProjectPage } from "./components/ProjectPage";
import { ProjectSecondaryMenu } from "./components/ProjectSecondaryMenu";
import { ProjectSettingsNotApproved } from "./components/ProjectSettingsNotApproved";
import { ProjectSettingsWaiting } from "./components/ProjectSettingsWaiting";
import { hasHeyGeniusDocTag } from "./helpers/helper";
import { useOpenPublication } from "./hooks/useOpenPublication";
import { useProjectDetails } from "./hooks/useProjectDetails";
import { useProjectInstance } from "./hooks/useProjectInstance";
import { usePublishUnpublish } from "./hooks/usePublishUnpublish";
import { useRegenerateOrApprove } from "./hooks/useRegenerateOrApprove";
import { ErrorFallback } from "components/ErrorBoundary/";
import { useTranslate } from "#src/hooks";
import { FeatureFlags } from "@/constants";
import { useFeatureFlag } from "hooks/useFeature/";
import { QuotaUpgradeModal } from "components/QuotaUpgradeModal";
import { SoftPaywallModal } from "components/SoftPaywallModal";
import { useSoftPaywall } from "@/providers/SoftPaywallProvider";
import { useProjectWizardStore } from "./useProjectWizardStore";

export const Detail = () => {
  const params = useParams<{ id: string; storyboardId?: string }>();
  const projectId = params.id;
  const storyboardIdFromUrl = params.storyboardId;
  const { theme } = useAntTheme();
  const t = useTranslate();
  const navigate = useNavigate();
  const location = useLocation();
  const fromStoryboardApproval =
    (location.state as { fromStoryboardApproval?: boolean } | undefined)
      ?.fromStoryboardApproval === true;
  const setWizardStep = useProjectWizardStore((s) => s.setStep);
  const {
    data,
    isLoading: isLoadingProject,
    refetch: refetchProjectDetail,
    isAccessDenied,
    error,
  } = useProjectDetails({ projectId });

  const {
    paywallFlow,
    showSoftPaywall,
    experiencesLeft,
    openSoftPaywall,
    dismissSoftPaywall,
  } = useSoftPaywall();

  // Re-run when openSoftPaywall identity changes (i.e. when eligibility/dismissed
  // state settles). The ref prevents reopening after the user explicitly dismisses.
  const hasDismissedRef = useRef(false);
  useEffect(() => {
    if (hasDismissedRef.current) return;
    openSoftPaywall();
  }, [openSoftPaywall]);

  const originalDismiss = dismissSoftPaywall;
  const handleDismiss = useCallback(() => {
    hasDismissedRef.current = true;
    originalDismiss();
  }, [originalDismiss]);

  const setSidebarCollapsed = useBoundStore(
    (state) => state.setSidebarCollapsed
  );
  const project = data?.data;
  const { currentOrganization: organization } = useSession();
  const { data: owner } = useOne<User>({
    resource: `api/users`,
    id: project?.owner_id ?? undefined,
    queryOptions: {
      enabled: !!project,
    },
    errorNotification: false,
  });

  useEffect(() => {
    setSidebarCollapsed(true);

    return () => {
      setSidebarCollapsed(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const storyboardId = useMemo(() => {
    if (!project?.storyboards) return undefined;
    if (storyboardIdFromUrl) {
      const exists = project.storyboards.some(
        (s) => s.id === storyboardIdFromUrl
      );
      return exists ? storyboardIdFromUrl : project.storyboards[0]?.id;
    }
    return project.storyboards[0]?.id;
  }, [project?.storyboards, storyboardIdFromUrl]);

  const storyboard = params.storyboardId
    ? project?.storyboards.find(({ id }) => id.startsWith(storyboardId))
    : project?.storyboards[0];

  const projectMedia = project?.media;
  const instanceId = useMemo(
    () =>
      projectMedia?.find(
        (media) => media.storyboard_settings_id === storyboardId
      )?.instance_id,
    [projectMedia, storyboardId]
  );

  const media = useMemo(
    () => projectMedia?.find((x) => x.instance_id === instanceId),
    [projectMedia, instanceId]
  );

  const errorStatus = [
    "StoryboardFailed",
    "SlideGenerationFailed",
    "MediaFailed",
    "DocumentError",
  ];

  const isProjectErrorStatus = errorStatus.includes(project?.status);

  const validStatus = [
    "MediaGenerating",
    "MediaRequested",
    "MediaGenerated",
    "MediaPending",
    "Published",
    "StoryboardApproved",
    "New",
    "AnalyzingDocument",
  ];

  const isProjectValidStatus = validStatus.includes(project?.status);

  const pendingStatus = [
    "WaitingForSettings",
    "StoryboardGenerating",
    "StoryboardReviewing",
  ];

  const isProjectPendingStatus = pendingStatus.includes(project?.status);

  const storyboardPendingStatuses = [
    "StoryboardGenerating",
    "StoryboardReviewing",
  ];
  const isStoryboardPending = storyboardPendingStatuses.includes(
    project?.status
  );

  const creationDate = project?.created;

  const isDocHeyGenius = hasHeyGeniusDocTag(project?.tags);
  const isAudioOnly = project?.settings?.media_generation_mode === "Audio";

  const {
    isLive,
    isPublicationOutOfDate,
    isMediaOutOfDate,
    isOutOfDate,
    isOutOfDateStatusLoading,
  } = useProjectInstance({
    projectId,
    instanceId,
    mediaId: media?.id,
  });

  const { publishedPublicationUrl } = useOpenPublication({
    projectId,
    publicationId: instanceId,
    requiresAccessToken: false,
    instanceSlug: media?.instance_slug,
    organizationSlug: organization?.slug,
    mediaStatus: media?.status,
    isLive,
    isDocHeyGenius,
    isAudioOnly,
    trackingPage: "Project",
  });

  const isNewProjectLayout = useFeatureFlag(
    FeatureFlags.NewProjectDetailLayout
  );

  const {
    publishUnpublish,
    republish,
    isMutatingStatus: isUnpublishing,
    showQuotaModal,
    dismissQuotaModal,
  } = usePublishUnpublish(projectId, instanceId);

  const handleUnpublish = async () => {
    await publishUnpublish();
  };

  const handlePublish = async () => {
    return publishUnpublish();
  };

  const handleRepublish = async () => {
    const { success } = await republish();
    return success;
  };

  const { regenerateOrApprove } = useRegenerateOrApprove({
    projectId,
    storyboardSettingsId: media?.storyboard_settings_id,
    storyboardStatus: storyboard?.status,
    mediaId: media?.id,
    mediaStatus: media?.status,
    refetchProjectDetail,
  });

  const handleRegenerate = async () => {
    return regenerateOrApprove();
  };

  const disablePublish = useMemo(
    () =>
      !media ||
      media.created === null ||
      media.status === "Generating" ||
      media.status === "Pending" ||
      media.status === "Draft",
    [media]
  );

  const headerButtons = isLoadingProject ? (
    <></>
  ) : (
    <Flex align="center">
      {project && isProjectValidStatus && (
        <InstanceSwitch
          projectDetails={{
            projectId,
            storyboardId,
            storyboards: project.storyboards,
          }}
        />
      )}
      <ProjectSecondaryMenu
        project={project}
        storyboardId={storyboardId}
        instanceId={instanceId}
      />
    </Flex>
  );

  const needsSettingsApproval =
    isNewProjectLayout &&
    project &&
    storyboardId &&
    (project.status === "WaitingForSettings" ||
      storyboard?.status === "WaitingUserInput");

  const needsStoryboardReview =
    isNewProjectLayout &&
    project &&
    storyboardId &&
    project.status === "StoryboardReviewing" &&
    storyboard?.status === "Reviewing" &&
    !fromStoryboardApproval;

  useEffect(() => {
    if (needsSettingsApproval) {
      setWizardStep("Review");
      navigate(`/media/projects/${projectId}/${storyboardId}/setup`, {
        replace: true,
      });
    }
  }, [needsSettingsApproval, setWizardStep, navigate, projectId, storyboardId]);

  useEffect(() => {
    if (needsStoryboardReview) {
      const scriptRoute = instanceId
        ? `/media/projects/${projectId}/${storyboardId}/${instanceId}/script`
        : `/media/projects/${projectId}/${storyboardId}/script`;
      navigate(scriptRoute, { replace: true });
    }
  }, [needsStoryboardReview, navigate, projectId, storyboardId, instanceId]);

  useEffect(() => {
    if (fromStoryboardApproval) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [fromStoryboardApproval, navigate, location.pathname]);

  if (isAccessDenied) {
    return (
      <ErrorFallback
        error={error}
        resetError={refetchProjectDetail}
        title={t("media.projects.detail.accessDenied")}
      />
    );
  }

  // New full-screen layout: bypass the Refine Show wrapper entirely
  if (
    isNewProjectLayout &&
    project &&
    !isProjectErrorStatus &&
    (!isProjectPendingStatus || isStoryboardPending)
  ) {
    return (
      <Authenticated key="project_detail">
        <HtmlPageTitle title={project.title} analyticsTitleKey="project" />
        <ProjectPage
          projectDetails={{
            project,
            projectId,
            instanceId,
            storyboardId,
            organizationSlug: organization?.slug,
            media,
            owner: owner?.data,
            isLive,
            isDocHeyGenius,
            isAudioOnly,
            publishedPublicationUrl,
            disablePublish,
            creationDate,
            isUnpublishing,
            isPublicationOutOfDate,
            isMediaOutOfDate,
            isOutOfDate,
            isOutOfDateStatusLoading,
            onRepublish: handleRepublish,
            onRegenerate: handleRegenerate,
          }}
          onUnpublish={handleUnpublish}
          onPublish={handlePublish}
          refetchProjectDetail={refetchProjectDetail}
        />
        <QuotaUpgradeModal open={showQuotaModal} onClose={dismissQuotaModal} />
        <SoftPaywallModal
          open={showSoftPaywall}
          onClose={handleDismiss}
          experiencesLeft={experiencesLeft}
          paywallFlow={paywallFlow}
        />
      </Authenticated>
    );
  }

  return (
    <Authenticated key="project_detail">
      {project && (
        <HtmlPageTitle title={project.title} analyticsTitleKey="project" />
      )}
      <Show
        goBack={null}
        isLoading={isLoadingProject}
        title={project ? <ProjectHeaderNew /> : <></>}
        headerButtons={headerButtons}
        contentProps={{
          style: {
            backgroundColor: theme.colorBgShowWrapper,
            border: "none",
            boxShadow: "none",
          },
          bodyStyle: {
            padding: 0,
          },
        }}
        breadcrumb={<></>}
      >
        {/* Project with error */}
        {project && isProjectErrorStatus && (
          <ProjectError
            projectDetails={{
              mediaId: media?.id,
              projectId,
              mediaStatus: media?.status,
            }}
            refetchProjectDetail={refetchProjectDetail}
          />
        )}
        {/* Settings not approved*/}
        {project && project?.status === "WaitingForSettings" && (
          <ProjectSettingsNotApproved
            projectId={projectId}
            storyboardId={storyboardId}
          />
        )}

        {/* New project waiting storyboard to be approved */}
        {project &&
          (project?.status === "StoryboardGenerating" ||
            project?.status === "StoryboardReviewing") && (
            <ProjectSettingsWaiting
              projectDetails={{
                projectId,
                projectType: project.type,
                storyboardStatus: storyboard.status,
                instanceId,
                storyboardId,
              }}
            />
          )}
        {/* Normal project view */}
        {project && !isProjectErrorStatus && !isProjectPendingStatus && (
          <ProjectPage
            projectDetails={{
              project,
              projectId,
              instanceId,
              storyboardId,
              organizationSlug: organization?.slug,
              media,
              owner: owner?.data,
              isLive,
              isDocHeyGenius,
              isAudioOnly,
              publishedPublicationUrl,
              disablePublish,
              creationDate,
              isUnpublishing,
              isPublicationOutOfDate,
              isMediaOutOfDate,
              isOutOfDate,
              isOutOfDateStatusLoading,
              onRepublish: handleRepublish,
              onRegenerate: handleRegenerate,
            }}
            onUnpublish={handleUnpublish}
            onPublish={handlePublish}
            refetchProjectDetail={refetchProjectDetail}
          />
        )}
      </Show>
      <QuotaUpgradeModal open={showQuotaModal} onClose={dismissQuotaModal} />
      <SoftPaywallModal
        open={showSoftPaywall}
        onClose={handleDismiss}
        experiencesLeft={experiencesLeft}
        paywallFlow={paywallFlow}
      />
    </Authenticated>
  );
};
